/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { useState, useEffect } from 'react';
import { message } from 'antd';
import { SelectValue } from 'antd/lib/select';
import { Button } from '../../_shared/button';
import { Select } from '../../_shared/select';
import { TableListHeader, TableList } from '../../_shared/table-list';
import { get } from 'lodash';
import moment from 'moment';
import { getAccelData, getAccelDevices } from '../../_shared/services/publish.service';
import { exportToCsv, sortAlphaNumeric } from '../../utils';

const downloadAccelCsv = (record: {}) => {
  const deviceId = get(record, 'data.device_id', get(record, 'coreid', 'unknown'));
  const trigger = get(record, 'data.trigger', 'unknown');
  const start: number = get(record, 'data.start', 0);
  const timestamp = start
    ? moment.unix(start).format('YYYYMMDD-HHmmss')
    : moment(get(record, 'createdAt')).format('YYYYMMDD-HHmmss');
  const filename = `${deviceId}_${trigger}_${timestamp}.csv`;

  const xyz: Array<{ x: number; y: number; z: number }> = get(record, 'data.xyz', []);
  const rows = [['x', 'y', 'z'], ...xyz.map(({ x, y, z }) => [x, y, z])];
  exportToCsv(filename, rows);
};

export const AccelData = () => {
  const [devices, setDevices] = useState<string[]>([]);

  useEffect(() => {
    getAccelDevices()
      .then(list => setDevices(sortAlphaNumeric(list)))
      .catch(err => message.error(`Failed to load devices: ${err.message}`));
  }, []);

  const [state, setState] = useState({
    loading: false,
    coreid: '',
    records: [] as {}[],
  });

  const fetchRecords = async (coreid: string) => {
    if (!coreid) return;
    try {
      setState(s => ({ ...s, loading: true, coreid, records: [] }));
      const records = await getAccelData(coreid);
      setState(s => ({ ...s, records, loading: false }));
    } catch (err) {
      message.error(`Failed to load data: ${err.message}`);
      setState(s => ({ ...s, loading: false }));
    }
  };

  const columns = [
    {
      title: 'Device ID',
      dataIndex: ['data', 'device_id'],
      width: 280,
      render: (_: string, row: {}) => get(row, 'data.device_id', get(row, 'coreid', '--')),
    },
    {
      title: 'Trigger',
      dataIndex: ['data', 'trigger'],
      width: 150,
      render: (_: string, row: {}) => get(row, 'data.trigger', '--'),
    },
    {
      title: 'Eq Index',
      dataIndex: ['data', 'eq'],
      width: 100,
      render: (_: string, row: {}) => {
        const eq = get(row, 'data.eq');
        return eq !== undefined ? eq : '--';
      },
    },
    {
      title: 'Start',
      dataIndex: ['data', 'start'],
      width: 200,
      render: (_: string, row: {}) => {
        const start: number = get(row, 'data.start', 0);
        return start ? moment.unix(start).format('YYYY-MM-DD HH:mm:ss') : '--';
      },
    },
    {
      title: 'Samples',
      dataIndex: ['data', 'xyz'],
      width: 100,
      render: (_: string, row: {}) => {
        const xyz = get(row, 'data.xyz', []);
        return Array.isArray(xyz) ? xyz.length : '--';
      },
    },
    {
      title: 'Download',
      dataIndex: '',
      width: 160,
      render: (_: string, row: {}) => (
        <Button title="Download CSV" icon="download" onClick={() => downloadAccelCsv(row)} />
      ),
    },
  ];

  return (
    <div>
      <TableListHeader style={`justify-content: flex-start;`}>
        <Select
          css={css`width: 360px; margin-right: 10px;`}
          placeholder="Select device..."
          onChange={(v: SelectValue) => fetchRecords(v as string)}
          showSearch
          filterOption={(input: string, option: {}) => {
            const label = get(option, 'props.children', '') as string;
            return label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
          }}
        >
          {devices.map((coreid: string) => (
            <Select.Option key={coreid} value={coreid}>
              {coreid}
            </Select.Option>
          ))}
        </Select>
      </TableListHeader>

      <TableList
        loading={state.loading}
        columns={columns}
        canSelectRows={false}
        data={state.records as never[]}
        pageSize={100}
        subtractHeight={120}
        includeCount="Events"
      />
    </div>
  );
};
